/**
 * Shared Page Layout Component
 *
 * Unified layout structure for all static content pages (FAQ, About, Terms, Privacy).
 * Based on the FAQ page pattern with consistent spacing and structure.
 *
 * Issue #867: Shows standard TopBar for authenticated users, PublicHeader for guests.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import StaticFooter from './StaticFooter';
import PublicHeader from './PublicHeader';
import FloatingHomeButton from './FloatingHomeButton';
import { TopBar, BottomNav } from './index';

interface PageLayoutProps {
  children: ReactNode;
  maxWidth?: 'narrow' | 'standard' | 'wide'; // 3xl, 4xl, 5xl
  title?: string; // Page title for TopBar (authenticated users)
}

export default function PageLayout({ children, maxWidth = 'standard', title }: PageLayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const maxWidthClass = {
    narrow: 'max-w-3xl',   // About page - more personal/readable
    standard: 'max-w-4xl', // FAQ page - standard content width
    wide: 'max-w-5xl',     // Future use if needed
  }[maxWidth];

  return (
    <div className="min-h-screen bg-oro-bg text-text-primary">
      {/* Header - TopBar for authenticated, PublicHeader for guests (#867) */}
      {isAuthenticated ? (
        <TopBar
          title={title || 'ORO move'}
          onBack={() => navigate(-1)}
        />
      ) : (
        <PublicHeader />
      )}

      {/* Content - z-0 ensures it stays below navigation overlays (#707 follow-up) */}
      {/* pt-14 for TopBar clearance when authenticated, pb-20 for BottomNav on mobile */}
      <main className={`flex-1 relative z-0 ${isAuthenticated ? 'pt-14 pb-20 md:pb-0' : ''}`}>
        <div className={`${maxWidthClass} mx-auto py-16 px-4`}>
          {children}
        </div>
      </main>

      {/* Footer - add bottom padding on mobile for authenticated users so BottomNav doesn't cover links */}
      <div className={isAuthenticated ? 'pb-20 md:pb-0' : ''}>
        <StaticFooter />
      </div>

      {/* Bottom navigation for authenticated users - hidden on desktop (#1008) */}
      {isAuthenticated && <BottomNav />}

      {/* Floating Home Button - only show for unauthenticated users */}
      {!isAuthenticated && <FloatingHomeButton />}
    </div>
  );
}